import { useMemo, useState, useEffect } from 'react'
import { Form, Input } from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Head, usePage } from '@inertiajs/react'

import AdminLayout from '@/components/layouts/AdminLayout'
import { PageHeader } from '@/components/patterns'
import {
  Button,
  ConfirmModal,
  Badge,
  Drawer,
  Pagination,
  Table,
  TableActions,
  message,
} from '@/components/ui'
import { Icon } from '@/components/icons'
import { PlusCircleIcon } from '@/components/icons/EventsIcons'
import { api } from '@/lib/apiClient'
import { keys } from '@/lib/queryKeys'
import Tooltip from '@/components/Tooltip'

type CategoryRecord = {
  id: string
  name: string
  slug: string
  events_count: number
  events: {
    id: string
    name: string
  }[]
}

type CategoryPayload = {
  category: {
    name: string
  }
}

type CategoryMutationResponse = {
  success: boolean
  category?: CategoryRecord
  errors?: string[]
}

type PageProps = {
  categories?: CategoryRecord[]
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!(error instanceof Error)) return fallback
  const candidate = error as Error & { data?: { errors?: string[] } }
  return candidate.data?.errors?.join(', ') || error.message || fallback
}

export default function CategoryIndexPage() {
  const { categories: initialCategories = [] } = usePage<PageProps>().props
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [currentCategory, setCurrentCategory] = useState<CategoryRecord | null>(null)
  const [createForm] = Form.useForm<{ name: string }>()
  const [editForm] = Form.useForm<{ name: string }>()

  const categoriesQuery = useQuery({
    queryKey: keys.categories.list(),
    queryFn: () => api.get<CategoryRecord[]>('/admin/categories'),
    initialData: initialCategories,
    initialDataUpdatedAt: Date.now(),
  })

  const categories = useMemo(
    () => [...(categoriesQuery.data || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [categoriesQuery.data]
  )

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(categories.length / pageSize))
    if (currentPage > maxPage) {
      setCurrentPage(maxPage)
    }
  }, [categories.length, currentPage, pageSize])

  const startIndex = (currentPage - 1) * pageSize
  const paginatedCategories = categories.slice(startIndex, startIndex + pageSize)

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page)
    setPageSize(size)
  }

  const createCategoryMutation = useMutation({
    mutationFn: (values: { name: string }) =>
      api.post<CategoryMutationResponse, CategoryPayload>('/admin/categories', {
        category: { name: values.name.trim() },
      }),
    onSuccess: (response) => {
      if (!response.success) {
        message.error(response.errors?.join(', ') || 'Failed to create category')
        return
      }
      categoriesQuery.refetch()
      createForm.resetFields()
      setCreateDrawerOpen(false)
      message.success('Category created successfully')
    },
    onError: (error) => {
      message.error(getErrorMessage(error, 'Failed to create category'))
    },
  })

  const updateCategoryMutation = useMutation({
    mutationFn: (values: { name: string }) => {
      if (!currentCategory?.id) throw new Error('No category selected')
      return api.patch<CategoryMutationResponse, CategoryPayload>(`/admin/categories/${currentCategory.id}`, {
        category: { name: values.name.trim() },
      })
    },
    onSuccess: (response) => {
      if (!response.success) {
        message.error(response.errors?.join(', ') || 'Failed to update category')
        return
      }
      categoriesQuery.refetch()
      editForm.resetFields()
      setEditDrawerOpen(false)
      setCurrentCategory(null)
      message.success('Category updated successfully')
    },
    onError: (error) => {
      message.error(getErrorMessage(error, 'Failed to update category'))
    },
  })

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => api.delete<{ success: boolean; errors?: string[] }>(`/admin/categories/${id}`),
    onSuccess: (response) => {
      if (!response.success) {
        message.error(response.errors?.join(', ') || 'Failed to delete category')
        return
      }
      categoriesQuery.refetch()
      setDeleteModalOpen(false)
      setCurrentCategory(null)
      message.success('Category deleted successfully')
    },
    onError: (error) => {
      message.error(getErrorMessage(error, 'Failed to delete category'))
      setDeleteModalOpen(false)
      setCurrentCategory(null)
    },
  })

  const openEditDrawer = (category: CategoryRecord) => {
    setCurrentCategory(category)
    editForm.setFieldsValue({ name: category.name })
    setEditDrawerOpen(true)
  }

  const openDeleteModal = (category: CategoryRecord) => {
    setCurrentCategory(category)
    setDeleteModalOpen(true)
  }

  return (
    <AdminLayout>
      <Head title="Categories" />
      <div className="w-full bg-[#f5f5f5]">
        <PageHeader
          title="Event Categories"
          subtitle="Manage reusable category tags for events."
          actions={
            <Button
              variant="primary"
              icon={<PlusCircleIcon />}
              onClick={() => setCreateDrawerOpen(true)}
            >
              Add Category
            </Button>
          }
        />

        <div className="px-6 pb-6">
          <Table<CategoryRecord>
            className="ava-data-table"
            rowKey="id"
            dataSource={paginatedCategories}
            loading={categoriesQuery.isLoading}
            columns={[
              {
                title: 'Category Name',
                dataIndex: 'name',
                key: 'name',
                sorter: (a, b) => a.name.localeCompare(b.name),
              },
              // {
              //   title: 'Slug',
              //   dataIndex: 'slug',
              //   key: 'slug',
              // },
              {
                title: 'Assigned Events',
                key: 'events',
                sorter: (a, b) => a.events_count - b.events_count,
                render: (_value, record) => {
                  const assignedEvents = record.events || []

                  if (!assignedEvents.length) {
                    return <span className="text-sm text-gray-400">No events</span>
                  }

                  const visibleEvents = assignedEvents.slice(0, 5)
                  const hiddenEvents = assignedEvents.slice(5)

                  return (
                    <div className="flex flex-wrap items-center gap-1">
                      {visibleEvents.map((event) => (
                        <Badge key={event.id} variant="default">
                          {event.name}
                        </Badge>
                      ))}
                      {hiddenEvents.length > 0 && (
                        <Tooltip
                          title={hiddenEvents.map((event) => event.name).join(', ')}
                          placement="top"
                        >
                          <Badge variant="default">+{hiddenEvents.length}</Badge>
                        </Tooltip>
                      )}
                    </div>
                  )
                },
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_value, record) => (
                  <TableActions
                    actions={[
                      {
                        key: 'edit',
                        icon: <Icon name="edit" size={20} />,
                        onClick: () => openEditDrawer(record),
                        ariaLabel: 'Edit category',
                      },
                      {
                        key: 'delete',
                        icon: <Icon name="delete" size={20} />,
                        onClick: () => openDeleteModal(record),
                        ariaLabel: 'Delete category',
                        danger: true,
                      },
                    ]}
                  />
                ),
              },
            ]}
            pagination={false}
          />
          {categories.length > 0 && (
            <div className="mt-4">
              <Pagination
                current={currentPage}
                total={categories.length}
                pageSize={pageSize}
                onChange={handlePaginationChange}
                showSizeChanger
                showQuickJumper
              />
            </div>
          )}
        </div>
      </div>

      <Drawer
        title="Add Event Category"
        open={createDrawerOpen}
        onClose={() => {
          setCreateDrawerOpen(false)
          createForm.resetFields()
        }}
        onSubmit={() => createForm.submit()}
        submitText="Create Category"
        loading={createCategoryMutation.isPending}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={(values) => createCategoryMutation.mutate(values)}
        >
          <Form.Item
            label="Category Name"
            name="name"
            rules={[
              { required: true, message: 'Category name is required' },
              { max: 255, message: 'Category name is too long' },
            ]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title="Edit Event Category"
        open={editDrawerOpen}
        onClose={() => {
          setEditDrawerOpen(false)
          setCurrentCategory(null)
          editForm.resetFields()
        }}
        onSubmit={() => editForm.submit()}
        submitText="Save Changes"
        loading={updateCategoryMutation.isPending}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => updateCategoryMutation.mutate(values)}
        >
          <Form.Item
            label="Category Name"
            name="name"
            rules={[
              { required: true, message: 'Category name is required' },
              { max: 255, message: 'Category name is too long' },
            ]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>
        </Form>
      </Drawer>

      <ConfirmModal
        open={deleteModalOpen}
        title="Delete Category"
        confirmText="Delete"
        danger
        loading={deleteCategoryMutation.isPending}
        onClose={() => {
          setDeleteModalOpen(false)
          setCurrentCategory(null)
        }}
        onConfirm={() => {
          if (currentCategory?.id) {
            deleteCategoryMutation.mutate(currentCategory.id)
          }
        }}
      >
        Are you sure you want to delete "{currentCategory?.name}"? This will remove it from any
        linked events.
      </ConfirmModal>
    </AdminLayout>
  )
}
